import { useCallback, useEffect, type Dispatch, type MutableRefObject } from 'react';
import { getCountdownSeconds, getExpectedSentAtFromSubmittedAt } from 'utils/report';
import { env } from 'config/env';
import analytics from 'modules/analytics';
import { reporter } from 'modules/analytics/constants';
import { logger } from 'modules/logger';
import { useCrossTabEventsListener, NameSpaces } from 'modules/EventsManager';
import type { LoadedReportWorklistItem } from 'hooks/useCurrentCaseReport';
import { createAddendumFieldName } from './utils';
import { ADDENDUM_NAME_PLACEHOLDER, type ActionType } from './fieldsReducer';
import { ReportStatus, defaultUnsignedReportWarningDialogState } from '../state';
import type { ReportStatuses } from '../state';
import { SubmitType } from './types';
import type { useCountdown } from './useCountdown';

type StartCountdown = ReturnType<typeof useCountdown>['startCountdown'];

/**
 * Owns report status restoration: on case load, reconstruct the client `ReportStatus` (and any
 * in-progress addendum) from the server report/addendum timestamps, resuming a live submit/addendum
 * countdown where one is still running. Reads `worklistItem.report` and `countdownSeconds` only — no
 * editor-content coupling.
 *
 * Also resolves the running countdown: on expiry it settles the provisional status (and finalizes a
 * provisional addendum), and a cross-tab CANCEL_REPORT_COUNTDOWN reverts an in-flight submit/addendum.
 */
export interface UseReportStatusLifecycleParams {
  worklistItem: LoadedReportWorklistItem;
  previousWorklistItemRef: MutableRefObject<LoadedReportWorklistItem | null>;
  countdownSeconds: number | null;
  startCountdown: StartCountdown;
  setStatus: (status: ReportStatuses) => void;
  dispatch: Dispatch<ActionType>;
  isBypassAndSign: boolean;
  isBypassAndSignPrelim: boolean;
  isClaimedByMe: boolean;
  isAddendumLocked: boolean | undefined;
  isWorklistUnifiedFieldsEnabled: boolean;
  setHasReportContentChanged: (value: boolean) => void;
  setDraftReportWorklistSmid: (value: string | null) => void;
  setUnsignedReportWarningDialogState: (
    value: typeof defaultUnsignedReportWarningDialogState
  ) => void;
  // countdown resolution (tier 2)
  status: ReportStatuses;
  addendumSMID: Nullable<string>;
  me: Nullable<{ firstName?: Nullable<string>; lastName?: Nullable<string> }>;
  clearTimer: () => void;
  cancelCountdown: () => void;
  setPrelimReportWorklistSmid: (value: string | null) => void;
  setRightDrawerCatalogRefreshSequence: (updater: (previous: number) => number) => void;
  analyticsData: Record<string, unknown> | undefined;
}

export const useReportStatusLifecycle = ({
  worklistItem,
  previousWorklistItemRef,
  countdownSeconds,
  startCountdown,
  setStatus,
  dispatch,
  isBypassAndSign,
  isBypassAndSignPrelim,
  isClaimedByMe,
  isAddendumLocked,
  isWorklistUnifiedFieldsEnabled,
  setHasReportContentChanged,
  setDraftReportWorklistSmid,
  setUnsignedReportWarningDialogState,
  status,
  addendumSMID,
  me,
  clearTimer,
  cancelCountdown,
  setPrelimReportWorklistSmid,
  setRightDrawerCatalogRefreshSequence,
  analyticsData,
}: UseReportStatusLifecycleParams): void => {
  const processCountdownState = useCallback(
    (params: {
      type: SubmitType;
      remainingTime: number;
      isBypass: boolean;
      submittedAt?: Nullable<Date>;
      expectedSentAt: Nullable<Date>;
      sentAt: Nullable<Date>;
    }) => {
      const { type, remainingTime, isBypass, submittedAt, expectedSentAt, sentAt } = params;
      const isPrelim = type === SubmitType.Prelim;
      if (remainingTime > 0 && !isBypass && sentAt == null) {
        if (isWorklistUnifiedFieldsEnabled) {
          startCountdown({ expectedSentAt });
        } else {
          startCountdown({ submittedAt });
        }
        setStatus(isPrelim ? ReportStatus.ProvisionalPrelimSubmit : ReportStatus.ProvisionalSubmit);
      } else {
        setStatus(isPrelim ? ReportStatus.Idle : ReportStatus.Submitted);
      }
    },
    [isWorklistUnifiedFieldsEnabled, setStatus, startCountdown]
  );

  const restoreStatus = useCallback(() => {
    const { report } = worklistItem;

    if (countdownSeconds != null || report == null) return;

    const { addendums } = report;

    const latestAddendum = addendums.find((addendum) => addendum.sentAt == null);

    if (latestAddendum) {
      const { submittedAt, sentAt: sentToMirthAt, smid, content } = latestAddendum;
      const expectedAddendumSentAt = getExpectedSentAtFromSubmittedAt(submittedAt);
      const addendumSecondsFromServer = getCountdownSeconds(expectedAddendumSentAt);
      const addendumPayload = {
        addendumSMID: smid,
        addendum: {
          name: !isClaimedByMe
            ? createAddendumFieldName({
                lastName: latestAddendum.owner?.lastName,
                addendumNumber: addendums.length,
                createdDate: latestAddendum.updated,
                updatedDate: latestAddendum.updated,
                isAddendumLocked,
              })
            : ADDENDUM_NAME_PLACEHOLDER,
          content,
        },
        addendumSubmittedDate: submittedAt,
      };

      if (addendumSecondsFromServer == null) {
        setStatus(ReportStatus.AddendumAdd);
        dispatch({
          type: 'restoreAddendum',
          payload: addendumPayload,
        });
      } else if (addendumSecondsFromServer > 0 && !isBypassAndSign && sentToMirthAt == null) {
        if (isWorklistUnifiedFieldsEnabled) {
          startCountdown({ expectedSentAt: expectedAddendumSentAt });
        } else {
          startCountdown({ submittedAt });
        }
        setStatus(ReportStatus.ProvisionalAddendumSubmit);
        dispatch({
          type: 'restoreAddendum',
          payload: addendumPayload,
        });
      } else {
        setStatus(ReportStatus.Submitted);
      }

      return;
    }

    const { submittedAt, sentAt } = report;
    const prelimSubmittedAt =
      (report as typeof report & { prelimSubmittedAt?: Nullable<Date> }).prelimSubmittedAt ?? null;
    const expectedSubmitSentAt = getExpectedSentAtFromSubmittedAt(submittedAt);
    const expectedPrelimSentAt = getExpectedSentAtFromSubmittedAt(prelimSubmittedAt);
    const remainingSubmitTime = getCountdownSeconds(expectedSubmitSentAt);
    const remainingPrelimSubmitTime = getCountdownSeconds(expectedPrelimSentAt);

    if (remainingSubmitTime != null) {
      processCountdownState({
        type: SubmitType.Final,
        remainingTime: remainingSubmitTime,
        isBypass: isBypassAndSign,
        submittedAt,
        expectedSentAt: expectedSubmitSentAt,
        sentAt,
      });
      return;
    }

    if (remainingPrelimSubmitTime != null) {
      processCountdownState({
        type: SubmitType.Prelim,
        remainingTime: remainingPrelimSubmitTime,
        isBypass: isBypassAndSignPrelim,
        submittedAt: prelimSubmittedAt,
        expectedSentAt: expectedPrelimSentAt,
        sentAt,
      });
      return;
    }
    // This line allows us to easily mock a specified report status within storybook
    env.STORYBOOK_STORYSHOTS !== 'true' && setStatus(ReportStatus.Idle);
  }, [
    worklistItem,
    countdownSeconds,
    setStatus,
    isBypassAndSign,
    isBypassAndSignPrelim,
    isClaimedByMe,
    isAddendumLocked,
    isWorklistUnifiedFieldsEnabled,
    startCountdown,
    processCountdownState,
    dispatch,
  ]);

  useEffect(() => {
    if (worklistItem?.smid !== previousWorklistItemRef?.current?.smid) {
      previousWorklistItemRef.current = worklistItem;
      setHasReportContentChanged(false);
      setDraftReportWorklistSmid(null);
      setUnsignedReportWarningDialogState(defaultUnsignedReportWarningDialogState);
      restoreStatus();
      // Fields is keyed by case SMID, so each case remount resets this before the initial lifecycle
      // evaluation. A no-op restore is still a completed reconciliation.
      dispatch({ type: 'markInitialStateReconciled' });
    }
  }, [
    worklistItem,
    previousWorklistItemRef,
    setHasReportContentChanged,
    setDraftReportWorklistSmid,
    setUnsignedReportWarningDialogState,
    restoreStatus,
    dispatch,
  ]);

  // This effect updates the client state to represent the `sent_at` status to mirth
  // (after 3 minutes, the report is locked in and cannot be edited further)
  useEffect(() => {
    if (countdownSeconds == null || countdownSeconds > 0) return;
    clearTimer();

    if (
      status === ReportStatus.ProvisionalSubmit ||
      status === ReportStatus.ProvisionalAddendumSubmit
    ) {
      setStatus(ReportStatus.Submitted);
    } else if (status === ReportStatus.ProvisionalPrelimSubmit) {
      setStatus(ReportStatus.Idle);
      setPrelimReportWorklistSmid(worklistItem.smid);
    }

    if (status === ReportStatus.ProvisionalAddendumSubmit) {
      dispatch({
        type: 'finalizeProvisionalAddendum',
        payload: { submittingUserFirstName: me?.firstName, submittingUserLastName: me?.lastName },
      });
    }
  }, [
    countdownSeconds,
    status,
    setStatus,
    clearTimer,
    me,
    worklistItem,
    setPrelimReportWorklistSmid,
    dispatch,
  ]);

  useCrossTabEventsListener(NameSpaces.CANCEL_REPORT_COUNTDOWN, ({ payload }) => {
    if (payload.worklistItemSmid === worklistItem.smid) {
      cancelCountdown();

      logger.info(
        '[useReportStatusLifecycle] Cancelling report timeline due to cross window event',
        {
          smid: worklistItem.smid,
          status: status,
        }
      );

      if (status === ReportStatus.ProvisionalSubmit) {
        setRightDrawerCatalogRefreshSequence((previous) => previous + 1);
        setStatus(ReportStatus.Idle);
        analytics.track(reporter.usr.submitReportCancel, {
          ...analyticsData,
          smid: worklistItem?.report?.smid,
        });
      } else if (status === ReportStatus.ProvisionalAddendumSubmit) {
        setRightDrawerCatalogRefreshSequence((previous) => previous + 1);
        setStatus(ReportStatus.AddendumAdd);
        analytics.track(reporter.usr.submitReportCancel, {
          ...analyticsData,
          smid: addendumSMID,
        });
      }
    }
  });
};
